import {Camera} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {Button} from "@/components/ui/button";

export type CameraPermissionDialogProps = {
  open: boolean;
  title?: string;
  description: string;
  /** Shown under Use gallery on Android PWAs — gallery can still trigger process kill. */
  galleryMemoryHint?: string | null;
  onRetry: () => void;
  /** Start getUserMedia on pointerdown (Android user activation). */
  onRetryPointerDown?: () => void;
  onUseGallery: () => void;
  onDismiss: () => void;
};

export function CameraPermissionDialog({
  open,
  title = "Camera access needed",
  description,
  galleryMemoryHint,
  onRetry,
  onRetryPointerDown,
  onUseGallery,
  onDismiss,
}: CameraPermissionDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onDismiss();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Camera className="size-4 shrink-0" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <AlertDialogAction
            type="button"
            className="w-full sm:w-auto"
            onPointerDown={(event) => {
              event.preventDefault();
              onRetryPointerDown?.();
            }}
            onClick={(event) => {
              event.preventDefault();
              onRetry();
            }}
          >
            Try again
          </AlertDialogAction>
          <div className="flex w-full flex-col gap-1">
            <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={onUseGallery}>
              Use gallery
            </Button>
            {galleryMemoryHint ? (
              <p className="text-muted-foreground text-center text-xs">{galleryMemoryHint}</p>
            ) : null}
          </div>
          <AlertDialogCancel type="button" className="mt-0 w-full sm:w-auto">
            Not now
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
